import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { getPortfolioChartPartialList } from "store/slices/investment/investmentSlice";
import useDispatch from "hooks/useDispatch";
import { Box, CircularProgress, styled, useTheme, alpha } from "@mui/material";

const InvestmentBarChart = ({ investmentDetail }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);

  const [series, setSeries] = useState([
    { name: "Main", data: [], color: "#3498db" },
    { name: "BTC", data: [], color: "#F7931A" },
    { name: "Gold", data: [], color: "#f1c40f" },
  ]);

  const options = {
    chart: {
      id: "bar-chart",
      toolbar: { show: false },
      background: "transparent",
    },
    tooltip: { enabled: true, theme: "dark" },
    colors: series.map((s) => s.color),
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "40%",
        borderRadius: 6,
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: ["Portfolio"],
      labels: { show: false },
    },
    yaxis: {
      opposite: true,
      labels: {
        formatter: (value) => value?.toFixed(2),
        style: { colors: theme.palette.text.main },
      },
    },
    grid: {
      borderColor: "#ffffff",
      strokeDashArray: 3,
    },
    legend: {
      show: true,
      labels: {
        colors: theme.palette.text.main,
      },
    },
  };

  const fetchSeriesValue = async (coinName) => {
    const today = new Date();
    const lastWeek = new Date();
    lastWeek.setDate(today.getDate() - 7);

    const body = {
      id: investmentDetail?.objPortfolio?.id,
      coin: coinName,
      startDate: lastWeek.toISOString().split("T")[0],
      endDate: today.toISOString().split("T")[0],
    };

    const { payload } = await dispatch(getPortfolioChartPartialList(body));
    const list = payload?.data?.data?.list;

    const percentages = list?.map((i) => i.percentage);
    return percentages?.[percentages.length - 1];
  };

  const fetchSequential = async () => {
    try {
      setLoading(true);

      const mainValue = await fetchSeriesValue("Main");

      setSeries((prev) =>
        prev.map((s) =>
          s.name === "Main" ? { ...s, data: [{ x: 0, y: mainValue }] } : s
        )
      );

      setLoading(false); 

      fetchSeriesValue("BTC").then((btcValue) => {
        setSeries((prev) =>
          prev.map((s) =>
            s.name === "BTC" ? { ...s, data: [{ x: 0, y: btcValue }] } : s
          )
        );
      });

      fetchSeriesValue("Gold").then((goldValue) => {
        setSeries((prev) =>
          prev.map((s) =>
            s.name === "Gold"
              ? { ...s, data: [{ x: 0, y: goldValue }] }
              : s
          )
        );
      });
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSequential();
    // eslint-disable-next-line
  }, []);

  return (
    <Box position="relative">
      {loading && (
        <LoadingWrapper>
          <CircularProgress size={20} color="secondary" />
        </LoadingWrapper>
      )}

      <Chart options={options} series={series} type="bar" height={200} />
    </Box>
  );
};

const LoadingWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  height: "100%",
  backdropFilter: "blur(5px)",
  zIndex: 1,
  background: alpha(theme.palette.secondary.main, 0.1),
  borderRadius: "16px",
}));

export default InvestmentBarChart;
